import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { useTranslation } from "react-i18next";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../Helper/DigitalTwin/DataFile";
import { usePagesContent, useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import DeviceIDLink from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import { useNavigate } from "react-router-dom";
import WarningIcon from "@mui/icons-material/Warning";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { getSmartAlertsList, smartAlertsListAction } from "../../../Api/SmartAlertsAnalyticsApi";
import CODE from "../../../Static/Constants/StatusCodes";
import {
  SMART_ALERTS_META_INFO_FIELDS,
  deriveMetaInfoFields,
  pickMetaInfoValues,
} from "../../../Helper/Analytics/tableMetaInfo";

const SEVERITY_SORT_COLUMN_MAP = {
  Critical:        "Critical",
  "High Severity": "High",
  Warning:         "Warning",
};

const DEFAULT_SORT = [
  { column: "Critical", direction: "DESC" },
  { column: "High",     direction: "DESC" },
  { column: "Warning",  direction: "DESC" },
];

// metaInfoFields comes from the caller rather than being fixed here, so the row
// carries whatever entity attributes the table is configured to show.
const transformRows = (apiRows, metaInfoFields) =>
  apiRows.map((row, index) => {
    const metrics = row.metricsArray || [];
    const getVal  = (type) => metrics.find((m) => m.type === type)?.value ?? 0;
    return {
      id:                 row.xLabel,
      key:                index,
      deviceID:           row.xLabel,
      ...pickMetaInfoValues(row, metaInfoFields),
      Critical:           getVal("Critical"),
      "High Severity":    getVal("High Severity") || getVal("High"),
      Warning:            getVal("Warning") || getVal("Medium") || getVal("Low") || 0,
    };
  });

const MostCriticalVehiclesTable = ({ startDate, endDate, globalFilters = {} }) => {
  const { t }            = useTranslation();
  const pagesContent     = usePagesContent();
  const navigate         = useNavigate();
  const { baseEntityTypePluralLable, baseEntityTypeLable } = useBaseEntityInfo();

  const tableMeta = pagesContent?.metaInfo?.table?.mostCriticalVehiclesTable;
  // What the table asks the API for, and reads back, follows its configured columns.
  const metaInfoFields = useMemo(
    () => deriveMetaInfoFields(tableMeta?.columnsConfig, SMART_ALERTS_META_INFO_FIELDS),
    [tableMeta]
  );

  const [loading, setLoading]             = useState(true);
  const [vehicleData, setVehicleData]     = useState([]);
  const [total, setTotal]                 = useState(0);
  const [pageNumber, setPageNumber]       = useState(1);
  const [rowsPerPage]                     = useState(10);
  const [sortBy, setSortBy]               = useState([]);

  const [initialColumns, setInitialColumns]                       = useState([]);
  const [tableColumns, setTableColumns]                           = useState([]);
  const [selectedColumnKeys, setSelectedColumnKeys]               = useState([]);
  const [defaultSelectedColumnKeys, setDefaultSelectedColumnKeys] = useState([]);
  const [columnOrder, setColumnOrder]                             = useState([]);

  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [isSelectAll, setIsSelectAll]                   = useState(false);

  const filterContextRef  = useRef({ startDate: null, endDate: null, globalFilters: null });
  const skipNextFetchRef  = useRef(false);

  const onBatteryIDClick = React.useCallback((record) => {
    const params = {
      batteryID: record.id,
      deviceID: record.deviceID || record.licensePlateNumber,
      batterySearchedGlobal: "false",
      prevLink: "/",
      navigateFromTab: true,
      tabValue: "alerts",
      alertsSubTab: "platform-alerts",
    };
    encryptTheParams(params, navigate, true);
  }, [navigate]);

  const customRenderers = React.useMemo(() => ({
    "device-id-link": (record, config) => (
      <DeviceIDLink
        record={record}
        config={config}
        onTextClick={() => onBatteryIDClick(record)}
      />
    ),
  }), [onBatteryIDClick]);

  const setupTableColumns = React.useCallback(() => {
    if (!tableMeta) return;
    const availableColumns = getColumnsConfig({
      tableColumns: tableMeta.columnsConfig,
      tableColumnOrder: tableMeta.columnOrdering,
      customRenderers,
      t,
    });
    setInitialColumns(availableColumns);
    const defaultSelected = availableColumns.slice(0, MAX_COLUMN_SELECTION_COUNT);
    const defaultKeys     = defaultSelected.map((col) => col.dataIndex);
    setTableColumns(defaultSelected);
    setSelectedColumnKeys(defaultKeys);
    setDefaultSelectedColumnKeys(defaultKeys);
    setColumnOrder(defaultSelected.map((col) => col.headerLabel));
  }, [tableMeta, customRenderers, t]);

  useEffect(() => {
    // Wait for the org meta to settle before the first fetch. It decides which
    // metaInfo this request asks for, so a fetch that beats it asks for nothing
    // and paints em dashes before repainting.
    if (!pagesContent?.mounted) return;
    if (!startDate || !endDate) {
      setLoading(false);
      return;
    }

    // Skip this run if it was triggered solely by a pagination reset from a filter change
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    // Detect filter context change and reset pagination for this fetch
    const prev = filterContextRef.current;
    const filterChanged =
      prev.startDate !== startDate ||
      prev.endDate !== endDate ||
      prev.globalFilters !== globalFilters;

    if (filterChanged) {
      filterContextRef.current = { startDate, endDate, globalFilters };
    }

    const fetchPage = filterChanged ? 1 : pageNumber;
    const fetchSort = filterChanged ? [] : sortBy;

    if (filterChanged) {
      if (pageNumber !== 1) { setPageNumber(1); skipNextFetchRef.current = true; }
      if (sortBy.length !== 0) { setSortBy([]); skipNextFetchRef.current = true; }
      setTotalSelectedRowKeys(new Set());
      setIsSelectAll(false);
    }

    let cancelled = false;
    setLoading(true);

    const apiSortBy = fetchSort.length > 0
      ? fetchSort.map((s) => ({ column: SEVERITY_SORT_COLUMN_MAP[s.column] || s.column, direction: s.direction }))
      : DEFAULT_SORT;

    getSmartAlertsList(globalFilters, {
      startDate,
      endDate,
      select:         ["GeneratedAlertsCount"],
      groupBy:        ["batteryID", "AlertSeverityName"],
      metaInfo:       metaInfoFields,
      sortBy:         apiSortBy,
      limit:          20,
      skipPagination: false,
      pageCount:      rowsPerPage,
      pageNum:        fetchPage,
    }).then((result) => {
      if (cancelled) return;

      if (result?.responseStatus?.code === CODE.SUCCESS) {
        const rows  = result.response?.data || [];
        const pInfo = result.response?.paginationInfo || {};
        setVehicleData(transformRows(rows, metaInfoFields));
        setTotal(pInfo.total ?? rows.length);
      } else {
        setVehicleData([]);
        setTotal(0);
      }
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [startDate, endDate, globalFilters, pageNumber, sortBy, metaInfoFields, pagesContent?.mounted]);

  useEffect(() => {
    setupTableColumns();
  }, [setupTableColumns]);

  const onColumnOrderChange = (newTableColumns) => {
    setColumnOrder(newTableColumns.map((col) => col.headerLabel));
  };

  const applySelectedColumns = (newSelectedKeys) => {
    const filtered = initialColumns.filter((col) => newSelectedKeys.includes(col.dataIndex));
    setTableColumns(filtered);
    setSelectedColumnKeys(newSelectedKeys);
    setColumnOrder(filtered.map((col) => col.headerLabel));
  };

  const handlePageChange = (page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  };

  const handleSortChange = (key, order) => {
    setSortBy([{ column: key, direction: order === "asc" ? "ASC" : "DESC" }]);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  const handleClearSelection = () => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  const onSelectChange = (record, selected) => {
    const updated = new Set(totalSelectedRowKeys);
    if (selected) {
      updated.add(record.id);
    } else {
      updated.delete(record.id);
      if (isSelectAll) setIsSelectAll(false);
    }
    setTotalSelectedRowKeys(updated);
  };

  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(selected ? new Set(selectedRows.map((r) => r?.id)) : new Set());
  };

  const getCurrentSorting = () => {
    if (sortBy.length > 0) {
      const first = sortBy[sortBy.length - 1];
      return { sortBy: first.column, orderBy: first.direction.toLowerCase() };
    }
    return {};
  };

  const getCSVData = async () => {
    const selectedIds = Array.from(totalSelectedRowKeys);
    const apiSortBy = sortBy.length > 0
      ? sortBy.map((s) => ({ column: SEVERITY_SORT_COLUMN_MAP[s.column] || s.column, direction: s.direction }))
      : DEFAULT_SORT;

    const csvColumns = tableColumns.map((col) => ({
      dataIndex:   col.dataIndex === "id" ? "xLabel" : col.dataIndex,
      headerLabel: col.headerLabel || col.title,
    }));

    const result = await smartAlertsListAction(globalFilters, {
      actionType:  "download-csv",
      startDate,
      endDate,
      select:      ["GeneratedAlertsCount"],
      groupBy:     ["batteryID", "AlertSeverityName"],
      metaInfo:    metaInfoFields,
      sortBy:      apiSortBy,
      limit:       20,
      selectAll:   isSelectAll || selectedIds.length === 0,
      resourceIDs: isSelectAll ? [] : selectedIds,
      fileName:    "most_critical_vehicles",
      columns:     csvColumns,
      columnOrder: csvColumns.map((c) => c.headerLabel),
    });
    return result?.response?.data || [];
  };

  const handleSendEmail = async (emailTo, emailCc, subject, body) => {
    const apiSortBy = sortBy.length > 0
      ? sortBy.map((s) => ({ column: SEVERITY_SORT_COLUMN_MAP[s.column] || s.column, direction: s.direction }))
      : DEFAULT_SORT;

    await smartAlertsListAction(globalFilters, {
      actionType:  "mail",
      startDate,
      endDate,
      select:      ["GeneratedAlertsCount"],
      groupBy:     ["batteryID", "AlertSeverityName"],
      metaInfo:    metaInfoFields,
      sortBy:      apiSortBy,
      limit:       20,
      selectAll:   true,
      fileName:    "most_critical_vehicles",
      emailTo,
      emailCc,
      subject,
      body,
    });
  };

  if (!pagesContent?.mounted || !tableMeta) return null;

  return (
    <Box
      sx={{
        borderRadius: "8px",
        borderLeft: "3px solid #B50500",
        backgroundColor: "#FFF1F1",
        padding: "24px 32px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
        <PrimaryTooltip
          title={`${baseEntityTypePluralLable} with the highest number of critical alerts are shown first, followed by high-severity alerts and warnings`}
          placement="bottom"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon sx={{ color: "#B50500", fontSize: 20 }} />
            <Typography variant="fw600s18lh24cB50500">
              Most Critical {baseEntityTypePluralLable} in your Fleet
            </Typography>
          </Box>
        </PrimaryTooltip>
        <Typography variant="fw400s14lh24c3D4B5A" sx={{ color: "#B50500 !important" }}>
          Top 20 {baseEntityTypePluralLable.toLowerCase()} with the highest number of critical, high-severity, and warning alerts
        </Typography>
      </Box>

      <GenericDraggableTableView
        title={`${baseEntityTypeLable} list`}
        showPrimaryButton={false}
        count={total}
        tableColumns={tableColumns}
        selectedColumns={selectedColumnKeys}
        tableData={vehicleData}
        availableEmailDomains={pagesContent?.metaInfo?.globalConfig?.["email.domains"] || []}
        initialColumns={initialColumns}
        applySelectedColumns={applySelectedColumns}
        defaultSelectedColumns={defaultSelectedColumnKeys}
        isLoading={loading}
        pageNumber={pageNumber}
        rowsPerPage={rowsPerPage}
        total={total}
        disablePagination={false}
        disableSearch={true}
        disableFiltersSettings={true}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        applySort={handleSortChange}
        onPageChange={handlePageChange}
        initialSorting={getCurrentSorting()}
        onClearSelection={handleClearSelection}
        emptyMessage={`No critical ${baseEntityTypePluralLable.toLowerCase()} found`}
        containerStyles={{
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          padding: "12px 24px",
          overflow: "visible",
          maxWidth: "calc(100vw - 220px)",
        }}
        getTotalRowSelection={() => Array.from(totalSelectedRowKeys)}
        getTotalRowSelectionCount={() => isSelectAll ? total : totalSelectedRowKeys.size}
        rowSelection={{
          selectedRowKeys: Array.from(totalSelectedRowKeys),
          onSelect: onSelectChange,
          onSelectAll: onSelectAllChange,
        }}
        maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
        getCSVData={getCSVData}
        getCSVFileName={() => "most_critical_vehicles"}
        onSendEmail={handleSendEmail}
      />
    </Box>
  );
};

export default MostCriticalVehiclesTable;
